import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { adminLogin as apiLogin } from '../services/api';
import { canAccess, getDefaultPage } from '../config/rbac';

const AppContext = createContext(null);

export function AppProvider({ children }) {

  const [auth, setAuth] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('te_auth')) || null; } catch { return null; }
  });

  const login = useCallback(async (credentials) => {
    if (credentials?.email && credentials?.password) {
      try {
        const res = await apiLogin(credentials.email, credentials.password);
        const token = res.data?.token || res.token;
        const user  = res.data?.user  || res.admin;
        const session = {
          token,
          id:        user?.id,
          user:      user?.email || user?.name,
          email:     user?.email,
          name:      user?.name,
          role:               user?.role || 'Super Admin',
          mustChangePassword: user?.mustChangePassword || false,
          loginTime: Date.now(),
        };
        sessionStorage.setItem('te_auth', JSON.stringify(session));
        setAuth(session);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err.message || 'Login failed' };
      }
    }
    const session = {
      token: null, user: credentials?.name || credentials?.email,
      email: credentials?.email, role: credentials?.role || 'Super Admin', loginTime: Date.now(),
    };
    sessionStorage.setItem('te_auth', JSON.stringify(session));
    setAuth(session);
    return { ok: true };
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('te_auth');
    setAuth(null);
  }, []);

  const token = auth?.token || null;
  const role  = auth?.role  || 'Super Admin';

  const hasAccess = useCallback((page) => canAccess(role, page), [role]);

  const [toasts, setToasts] = useState([]);
  const toast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);
  const removeToast = useCallback(id => setToasts(prev => prev.filter(t => t.id !== id)), []);

  const [modal, setModal] = useState(null);
  const openModal  = useCallback((type, data) => setModal({ type, data }), []);
  const closeModal = useCallback(() => setModal(null), []);

  const [page, setPage] = useState(() => {
    const hash = window.location.hash.replace('#', '') || 'dashboard';
    return hash;
  });

  const navigate = useCallback((p) => {
    // Check access before navigating
    if (!canAccess(role, p)) {
      const def = getDefaultPage(role);
      window.location.hash = def;
      setPage(def);
      return;
    }
    window.location.hash = p;
    setPage(p);
  }, [role]);

  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash.replace('#', '') || 'dashboard';
      // Redirect if no access
      if (auth && !canAccess(auth.role || 'Super Admin', hash)) {
        const def = getDefaultPage(auth.role || 'Super Admin');
        window.location.hash = def;
        setPage(def);
        return;
      }
      setPage(hash);
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [auth]);

  return (
    <AppContext.Provider value={{
      auth, token, role, login, logout, hasAccess,
      toast, toasts, removeToast,
      modal, openModal, closeModal,
      page, navigate,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
